import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import HeroScene from "../components/HeroScene";
import { Photo } from "../components/Photo";
import { Container, Eyebrow, SectionHeader, CtaButton, Icon, useReveal } from "../components/primitives";
import { ServiceCard, ServiceModal, ProcessTimeline, CaseStudyCard } from "../components/blocks";
import { SERVICE_LINES, VERTICAL_SLUG, TRANSFORMATION_STORY, ECOSYSTEM, WHY_US } from "../content/site";
import { SERVICES } from "../content/services";
import { INDUSTRIES } from "../content/industries";
import { CASE_STUDIES, CASE_CATEGORIES } from "../content/caseStudies";

export function Hero() {
  const video = useRef(null);
  const [reduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    const onVis = () => { if (document.hidden) v.pause(); else if (!reduced) v.play().catch(() => {}); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [reduced]);
  return (
    <section className="relative overflow-hidden bg-navy text-white min-h-[100svh] flex items-center" data-testid="home-hero">
      <video ref={video} className={`absolute inset-0 h-full w-full object-cover object-[center_30%] ${reduced ? "" : "ken-burns"}`} src="/hero-bg.mp4" poster="/hero-bg-poster.jpg"
        autoPlay={!reduced} muted loop playsInline preload="auto" aria-hidden="true" data-testid="hero-video" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/80 to-[#0A1628]/30" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-[#0A1628]/60" aria-hidden="true" />
      <div className="absolute inset-0 bg-[#0A1628]/45 lg:bg-transparent" aria-hidden="true" />
      <div className="absolute inset-0 bg-grid-dark opacity-20" aria-hidden="true" />
      <div className="absolute inset-0 scanlines opacity-60 pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 opacity-25 lg:opacity-40 mix-blend-screen pointer-events-none"><HeroScene /></div>
      <Container className="relative py-28 lg:py-36 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.02] drop-shadow-[0_4px_30px_rgba(10,22,40,0.8)]">
            Technology That Turns <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1A6EFF] to-[#00D4FF]">Intelligence</span> Into Business Outcomes.
          </h1>
          <p className="mt-7 text-base md:text-lg text-white/75 leading-relaxed max-w-xl">
            TechAI Fortune combines technology consulting, AI, cloud and software engineering with workforce solutions — so the strategy, the build and the people who run it come from one accountable partner.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <CtaButton to="/contact" testId="hero-primary-cta">Book a Consultation <ArrowRight className="ml-2 h-4 w-4" /></CtaButton>
            <CtaButton to="/services" variant="outline" testId="hero-secondary-cta" className="glass-dark">Explore Services</CtaButton>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
            {["Business-first approach", "Consulting + delivery", "Technology + workforce"].map((t) => <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-[#00D4FF]" />{t}</span>)}
          </div>
        </div>
        <div className="hidden lg:flex lg:col-span-5 justify-end">
          <div className="glass-dark rounded-2xl px-5 py-4 font-mono text-xs text-white/85 leading-relaxed float-y" data-testid="hero-progression">
            <div className="eyebrow text-[#00D4FF] mb-2 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#00D4FF] animate-pulse" />Live intelligence layer</div>
            Data → Intelligence → Technology<br />→ Automation → People → Outcomes
          </div>
        </div>
      </Container>
      <a href="#who-we-are" aria-label="Scroll to content" className="absolute left-1/2 bottom-6 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors" data-testid="hero-scroll-cue">
        <span className="eyebrow">Scroll</span><span className="h-9 w-5 rounded-full border border-white/40 flex justify-center pt-1.5"><span className="h-1.5 w-1 rounded-full bg-[#00D4FF] float-y" /></span>
      </a>
    </section>
  );
}

export function WhoWeAre() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-light" id="who-we-are">
      <Container className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 reveal"><SectionHeader eyebrow="Who we are" title="A technology partner built around outcomes, not tools." desc="We are a U.S. technology consulting and workforce solutions company. Our teams design, build and run AI, cloud, data, automation and software — and place the people who make it work." /></div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {[["Consulting", "Strategy, architecture and roadmaps tied to business goals."], ["Engineering", "Delivery teams that ship production systems, not slide decks."], ["AI & Data", "Practical intelligence grounded in trusted data."], ["Workforce", "Staffing and managed workforce programs across industries."]].map(([t, d], i) => (
            <div key={t} className="bento reveal p-6" style={{ transitionDelay: `${i * 70}ms` }}><div className="eyebrow text-[#1A6EFF]">0{i + 1}</div><h3 className="mt-3 font-display text-lg font-bold">{t}</h3><p className="mt-2 text-sm text-muted-foreground">{d}</p></div>
          ))}
        </div>
      </Container>
    </section>
  );
}

export function Expertise() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white" id="expertise">
      <Container>
        <SectionHeader eyebrow="AI & technology expertise" title="Six areas of depth. One integrated team." desc="Each expertise area is delivered as advisory, implementation, migration or managed service." className="reveal" />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {SERVICES.map((s, i) => (
            <div key={s.slug} className="reveal" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`expertise-${s.slug}`}>
              <div className="flex items-center gap-3"><Icon name={s.icon} className="h-5 w-5 text-[#1A6EFF]" /><h3 className="font-display font-bold text-lg">{s.title}</h3></div>
              <ul className="mt-4 flex flex-wrap gap-2">{s.sub.map((x) => <li key={x} className="text-xs px-2.5 py-1 rounded-full border border-border bg-[#F8FAFF] text-[#0A1628]/80">{x}</li>)}</ul>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

export function ServiceCards() {
  const ref = useReveal(); const [open, setOpen] = useState(null);
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-navy text-white" id="services">
      <Container>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 reveal">
          <SectionHeader dark eyebrow="Services" title="Services engineered for measurable outcomes." />
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-[#00D4FF] hover:gap-3 transition-[gap]" data-testid="services-view-all">All services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} index={i} dark={i % 2 === 0} onOpen={setOpen} />)}
        </div>
      </Container>
      <ServiceModal service={open} onClose={() => setOpen(null)} />
    </section>
  );
}

export function ServiceLines() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-light-blue" id="service-lines">
      <Container>
        <SectionHeader eyebrow="Service lines & industry verticals" title="Workforce solutions mapped to the industries we serve." className="reveal" />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICE_LINES.map((l, i) => (
            <div key={l.title} className="bento reveal p-6" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`service-line-${i}`}>
              <h3 className="font-display font-bold text-lg">{l.title}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {l.verticals.map((v) => (
                  <Link key={v} to={`/industries/${VERTICAL_SLUG[v]}`} className="text-xs px-3 py-1.5 rounded-full border border-[#1A6EFF]/25 bg-white text-[#1A6EFF] hover:bg-[#1A6EFF] hover:text-white focus-visible:bg-[#1A6EFF] focus-visible:text-white outline-none transition-colors" data-testid={`vertical-tag-${VERTICAL_SLUG[v]}`}>{v}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-14 reveal">
          <div className="eyebrow text-muted-foreground mb-4">Industry verticals</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map((ind) => (
              <Link key={ind.slug} to={`/industries/${ind.slug}`} className="group flex items-center justify-between rounded-xl border border-border bg-white px-5 py-4 hover:border-[#1A6EFF] transition-colors" data-testid={`industry-link-${ind.slug}`}>
                <span className="flex items-center gap-3 font-semibold text-sm"><Icon name={ind.icon} className="h-5 w-5 text-[#1A6EFF]" />{ind.title}</span><ArrowRight className="h-4 w-4 text-[#1A6EFF] group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

export function HowWeWork() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white" id="how-we-work">
      <Container>
        <SectionHeader eyebrow="How we work" title="Discover → Strategy → Design → Build → Deploy → Optimize" desc="A delivery model that keeps strategy and engineering in the same room." className="reveal" />
        <div className="mt-16"><ProcessTimeline /></div>
      </Container>
    </section>
  );
}

export function TransformationStory() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-navy text-white relative overflow-hidden" id="transformation">
      <div className="absolute inset-0 bg-grid-dark opacity-30" />
      <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[400px] w-[800px] rounded-full bg-[#1A6EFF]/15 blur-[120px]" />
      <Container className="relative">
        <SectionHeader dark eyebrow="Digital transformation story" title="From raw data to business outcomes." align="center" className="reveal" />
        <ol className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {TRANSFORMATION_STORY.map((s, i) => (
            <li key={s.key} className="reveal relative glass-dark rounded-2xl p-6" style={{ transitionDelay: `${i * 110}ms` }} data-testid={`story-${s.key}`}>
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#1A6EFF] to-[#00D4FF] grid place-items-center font-mono text-xs font-bold text-white float-y" style={{ animationDelay: `${i * 0.6}s` }}>{i + 1}</div>
              <h3 className="mt-5 font-display font-bold text-lg">{s.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.desc}</p>
              {i < TRANSFORMATION_STORY.length - 1 && <ArrowRight className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#00D4FF]/60" aria-hidden="true" />}
            </li>
          ))}
        </ol>
      </Container>
    </section>
  );
}

export function CaseStudiesSection() {
  const ref = useReveal(); const [cat, setCat] = useState("All");
  const list = (cat === "All" ? CASE_STUDIES : CASE_STUDIES.filter((c) => c.category === cat)).slice(0, 3);
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-light" id="case-studies">
      <Container>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 reveal">
          <SectionHeader eyebrow="Case studies" title="Outcomes across AI, cloud, software, data, automation and staffing." desc="Placeholder studies are anonymized until client approval. Verified names and metrics will replace them." />
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A6EFF]">All case studies <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div role="tablist" aria-label="Filter case studies" className="mt-10 flex flex-wrap gap-2 reveal">
          {CASE_CATEGORIES.map((c) => <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)} data-testid={`home-case-filter-${c.toLowerCase()}`} className={`nav-label px-4 h-10 rounded-full border transition-colors ${cat === c ? "bg-[#0A1628] text-white border-[#0A1628]" : "bg-white border-border hover:border-[#1A6EFF]"}`}>{c}</button>)}
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">{list.map((c, i) => <CaseStudyCard key={c.id} c={c} index={i} />)}</div>
      </Container>
    </section>
  );
}

export function Ecosystem() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white relative overflow-hidden" id="ecosystem">
      <Container>
        <SectionHeader eyebrow="Technology ecosystem" title="A connected ecosystem of platforms and practices." desc="Categories shown reflect capabilities; vendor logos appear only with appropriate permissions." align="center" className="reveal" />
        <div className="mt-16 relative">
          <div className="absolute inset-0 grid place-items-center pointer-events-none" aria-hidden="true"><div className="h-[520px] w-[520px] rounded-full border border-dashed border-[#1A6EFF]/20 rotate-slow" /><div className="absolute h-[320px] w-[320px] rounded-full border border-[#00D4FF]/25" /></div>
          <div className="relative grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ECOSYSTEM.map((e, i) => (
              <div key={e.title} className="bento reveal p-6 glass" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`ecosystem-${e.title.toLowerCase().replace(/[^a-z]/g, "")}`}>
                <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-gradient-to-br from-[#1A6EFF] to-[#00D4FF]" /><h3 className="font-display font-bold">{e.title}</h3></div>
                <ul className="mt-3 flex flex-wrap gap-2">{e.items.map((x) => <li key={x} className="text-xs px-2.5 py-1 rounded-full bg-white border border-border">{x}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

export function WhyUs() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-light-blue" id="why-us">
      <Container className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 reveal">
          <SectionHeader eyebrow="Why TechAI Fortune" title="One partner for technology and the people who run it." />
          <div className="mt-8 rounded-2xl overflow-hidden depth-shadow-lg">
            <Photo name="securityTeam" ratio="4/3" parallax={8} particles treat="card" sizes="(max-width: 1024px) 100vw, 40vw">
              <div className="absolute left-5 right-5 bottom-5 text-white">
                <div className="eyebrow text-[#FF8A3D]">Trust · Security · Control</div>
                <div className="mt-1 font-display font-bold text-lg leading-tight">Enterprise-grade operations, monitored around the clock.</div>
              </div>
            </Photo>
          </div>
          <div className="mt-8"><CtaButton to="/about" variant="ghost" testId="why-about-cta">About the company</CtaButton></div>
        </div>
        <ul className="lg:col-span-7 space-y-3">
          {WHY_US.map((w, i) => (
            <li key={w.title} className="reveal flex gap-4 rounded-xl bg-white border border-border p-5" style={{ transitionDelay: `${i * 70}ms` }} data-testid={`why-item-${i}`}>
              <div className="h-8 w-8 rounded-full bg-[#1A6EFF] text-white grid place-items-center shrink-0"><Check className="h-4 w-4" /></div>
              <div><h3 className="font-semibold">{w.title}</h3><p className="text-sm text-muted-foreground mt-1">{w.desc}</p></div>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

export function WorkforceSolutions() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white" id="workforce">
      <Container className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="reveal"><SectionHeader eyebrow="Workforce solutions" title="Temporary, contract, direct hire and managed workforce programs." desc="Technology is only half the outcome. We recruit, screen and place administrative, healthcare, manufacturing, technology, finance and professional services talent — with compliance-aware workflows." />
          <div className="mt-8 flex flex-wrap gap-3"><CtaButton to="/contact?form=employer" testId="workforce-employer-cta">Request Staffing <ArrowRight className="ml-2 h-4 w-4" /></CtaButton><CtaButton to="/careers#apply" variant="ghost" testId="workforce-candidate-cta">Find a Role</CtaButton></div></div>
        <div className="reveal rounded-3xl overflow-hidden depth-shadow-lg" data-testid="workforce-visual">
          <Photo name="staffingTeam" ratio="4/3" parallax={10} treat="card" sizes="(max-width: 1024px) 100vw, 50vw">
            <div className="absolute inset-x-4 bottom-4 glass-dark rounded-2xl p-4">
              <div className="eyebrow text-[#00D4FF] mb-3">Industries we staff</div>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((ind) => (
                  <Link key={ind.slug} to={`/industries/${ind.slug}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/10 hover:bg-[#1A6EFF] rounded-full px-3 py-1.5 transition-colors" data-testid={`workforce-industry-${ind.slug}`}>
                    <Icon name={ind.icon} className="h-3.5 w-3.5" />{ind.title.replace(" Staffing", "")}
                  </Link>
                ))}
              </div>
            </div>
          </Photo>
        </div>
      </Container>
    </section>
  );
}
